import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Crea connessione Socket.io
  const connect = useCallback((token, isAdmin = false) => {
    if (socketRef.current?.connected) {
      console.log('Socket already connected');
      return socketRef.current;
    }

    console.log('Connecting to WebSocket server...');

    // Usa il protocollo e host corrente (stesso dominio)
    const socketUrl = `${window.location.protocol}//${window.location.host}`;
    console.log('Socket URL:', socketUrl);

    const newSocket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      path: '/socket.io/'
    });

    newSocket.on('connect', () => {
      console.log('WebSocket connected:', newSocket.id);
      setConnected(true);
      setConnectionError(null);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      setConnectionError(error.message);
      setConnected(false);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setConnected(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      setConnected(true);
      setConnectionError(null);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed');
      setConnectionError('Failed to reconnect');
    });

    socketRef.current = newSocket;
    return newSocket;
  }, []);

  // Disconnetti socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('Disconnecting WebSocket...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
    }
  }, []);

  // Join room del Becco (per utenti)
  const joinBeccoRoom = useCallback((beccoId) => {
    if (!socketRef.current) {
      console.warn('Cannot join room: socket not initialized');
      return;
    }

    if (!socketRef.current.connected) {
      console.warn('Cannot join room: socket not yet connected, waiting...');
      // Ritenta dopo un breve delay
      setTimeout(() => joinBeccoRoom(beccoId), 100);
      return;
    }

    console.log('Joining becco room:', beccoId);
    socketRef.current.emit('join-becco-room', beccoId);
  }, []);

  // Subscribe a evento
  // Eventi disponibili:
  // - 'status-updated': Cambio stato Becco
  // - 'custom-status-changed': Modifica stati custom
  // - 'event-changed': Creazione/modifica/cancellazione eventi
  // - 'room-joined': Conferma join room
  const on = useCallback((event, handler) => {
    if (!socketRef.current) {
      console.warn('Cannot subscribe to event: socket not initialized');
      return;
    }

    console.log('Subscribing to event:', event);
    socketRef.current.on(event, handler);

    // Return cleanup function
    return () => {
      console.log('Unsubscribing from event:', event);
      socketRef.current?.off(event, handler);
    };
  }, []);

  // Emit evento
  const emit = useCallback((event, data) => {
    if (!socketRef.current || !connected) {
      console.warn('Cannot emit event: socket not connected');
      return;
    }

    console.log('Emitting event:', event, data);
    socketRef.current.emit(event, data);
  }, [connected]);

  // Get socket instance
  const getSocket = useCallback(() => {
    return socketRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        console.log('Cleaning up socket on unmount');
        socketRef.current.disconnect();
      }
    };
  }, []);

  const value = {
    socket: socketRef.current,
    connected,
    connectionError,
    connect,
    disconnect,
    joinBeccoRoom,
    on,
    emit,
    getSocket
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
